import React, { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { LOGO } from "@/data/mock";
import { Logo } from "@/components/brand/Logo";
import { cn } from "@/lib/utils";

const fine = () => typeof window !== "undefined" && window.matchMedia?.("(pointer: fine)").matches && !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Premium $AURAMAX coin built from the flat original logo: CSS chrome rim + bevel + glass + sheen, gentle 3D tilt,
// float, soft Aura glow, desktop cursor parallax. Pauses offscreen, static under reduced-motion, falls back to the logo.
export function GenesisToken({ size = 160, className, parallax = true }) {
  const ref = useRef(null);
  const [paused, setPaused] = useState(false);
  const [broken, setBroken] = useState(false);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 60, damping: 18 });
  const sry = useSpring(ry, { stiffness: 60, damping: 18 });

  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) return undefined;
    const io = new IntersectionObserver(([e]) => setPaused(!e.isIntersecting), { threshold: 0.05 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!parallax || paused || !fine()) return undefined;
    const move = (e) => {
      const r = ref.current?.getBoundingClientRect();
      if (!r) return;
      const dx = (e.clientX - (r.left + r.width / 2)) / window.innerWidth;
      const dy = (e.clientY - (r.top + r.height / 2)) / window.innerHeight;
      ry.set(Math.max(-1, Math.min(1, dx * 2)) * 10);
      rx.set(Math.max(-1, Math.min(1, dy * 2)) * -8);
    };
    const leave = () => { rx.set(0); ry.set(0); };
    window.addEventListener("pointermove", move, { passive: true });
    window.addEventListener("pointerleave", leave);
    return () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerleave", leave); };
  }, [parallax, paused, rx, ry]);

  if (broken) return <div className={cn("grid place-items-center", className)} style={{ width: size, height: size }}><Logo size={size * 0.8} /></div>;
  const pad = size * 0.18;
  return (
    <div ref={ref} className={cn("coin-scene relative grid place-items-center", paused && "coin-paused", className)} style={{ width: size + pad * 2, height: size + pad * 2 }} data-testid="genesis-token" aria-hidden="true">
      <div className="coin-glow aura-radial" />
      <motion.div className="coin-float" style={{ rotateX: srx, rotateY: sry, transformStyle: "preserve-3d" }}>
        <div className="coin" style={{ width: size, height: size }}>
          <div className="coin-rim" />
          <div className="coin-edge" />
          <div className="coin-bevel" />
          <div className="coin-face"><img src={LOGO} alt="" loading="lazy" decoding="async" onError={() => setBroken(true)} /></div>
          <div className="coin-glass" />
          <div className="coin-sheen" />
        </div>
      </motion.div>
    </div>
  );
}
